/**
 * useEmptyState Hook
 *
 * Centralized hook for detecting empty states across the application.
 * Provides consistent logic for determining when to show empty state components.
 */

import { useMemo } from 'react';
import { useTimeSeriesData } from './useTimeSeriesStats';
import { useFixedSRSData } from './useFixedSRSData';
import { useDailyProgressData } from './useDailyProgressData';
import type { EmptyStateHookReturn } from '@/types/empty-state';

export function useEmptyState(targetLanguage: string = 'es'): EmptyStateHookReturn {
  // Get data from all relevant hooks with their loading states
  const timeSeriesData = useTimeSeriesData({ value: '7d', label: 'Last 7 days', days: 7 });
  const { dueWords, isLoading: srsLoading } = useFixedSRSData();
  const { stats: dailyProgress, isLoading: dailyLoading } = useDailyProgressData(targetLanguage);

  return useMemo(() => {
    // Aggregate loading states - if ANY hook is loading, we're loading
    const isLoading = timeSeriesData.isLoading || srsLoading || dailyLoading;

    // If data is still loading, don't make any determinations yet
    // Return safe defaults that won't trigger premature UI decisions
    if (isLoading) {
      return {
        isNewUser: false, // Default to false while loading to prevent flash
        isLoading: true,
        hasVocabularyData: false,
        hasActivityData: false,
        hasSRSData: false,
        hasConversationData: false,
        hasGrammarData: false,
      };
    }

    // Only evaluate user status when all data is ready
    const totalWords = timeSeriesData?.totalWords || 0;
    const chartDataLength = timeSeriesData?.chartData?.length || 0;
    const dueWordsCount = dueWords?.length || 0;
    const newWordsToday = dailyProgress?.newWordsToday || 0;

    return {
      // New user: absolutely no learning data anywhere
      isNewUser: totalWords === 0 && chartDataLength === 0 && dueWordsCount === 0 && newWordsToday === 0,

      // Loading complete
      isLoading: false,

      // Has vocabulary data: any words learned
      hasVocabularyData: totalWords > 0,

      // Has activity data: any chart data points
      hasActivityData: chartDataLength > 0,

      // Has SRS data: any flashcards created
      hasSRSData: dueWordsCount > 0,

      // Has conversation data: determined by new words today
      hasConversationData: newWordsToday > 0,

      // Grammar data (placeholder for future implementation)
      hasGrammarData: false,
    };
  }, [
    timeSeriesData.isLoading,
    timeSeriesData?.totalWords,
    timeSeriesData?.chartData?.length,
    srsLoading,
    dailyLoading,
    dueWords?.length,
    dailyProgress?.newWordsToday,
  ]);
}
